import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Wrench, Clock, CheckCircle, Users, CalendarDays, Cpu, UserCircle } from "lucide-react";
import Header from "../components/layout/Header";
import { StatCard, Badge, SearchInput, Button, Table } from "../components/ui/index";
import ActionMenu from "../components/ui/ActionMenu";
import { chantiersAPI, cascadeAPI } from "../api/services";
import { formatDate } from "../utils/dateUtils";
import { useAuth } from "../context/AuthContext";
import AddChantierModal from "../components/modals/AddChantierModal";
import EditChantierModal from "../components/modals/EditChantierModal";


export default function Chantiers() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, canDo, adminMode } = useAuth();
  const isMembre = user?.role === 'membre';
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [editItem, setEditItem] = useState(null);

  // Data state
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, enCours: 0, termines: 0, ouvriers: 0 });

  // Cascade filter state
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projets, setProjets] = useState([]);

  const [filterRevendeur, setFilterRevendeur] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterProjet, setFilterProjet] = useState("");
  const [cardFilter, setCardFilter] = useState("all");

  // Fetch chantiers
  const fetchChantiers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterRevendeur) params.revendeur = filterRevendeur;
      if (filterClient) params.client = filterClient;
      if (filterProjet) params.projet = filterProjet;

      const { data } = await chantiersAPI.getAll(params);
      const list = data.data || data;
      setChantiers(Array.isArray(list) ? list : []);

      // Compute stats
      const total = Array.isArray(list) ? list.length : 0;
      const enCours = Array.isArray(list) ? list.filter(c => c.etat === "En cours").length : 0;
      const termines = Array.isArray(list) ? list.filter(c => c.etat === "Terminé").length : 0;
      const ouvriers = Array.isArray(list) ? list.reduce((sum, c) => sum + (c.ouvriers || 0), 0) : 0;
      setStats({ total, enCours, termines, ouvriers });
    } catch (err) {
      console.error("Erreur chargement chantiers:", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterRevendeur, filterClient, filterProjet]);

  useEffect(() => { fetchChantiers(); }, [fetchChantiers]);

  // Load cascade revendeurs on mount
  useEffect(() => {
    cascadeAPI.revendeurs().then(res => {
      setRevendeurs(res.data.data || res.data || []);
    }).catch(console.error);
  }, []);

  // Load clients when revendeur changes (all clients if no revendeur selected)
  useEffect(() => {
    cascadeAPI.clients(filterRevendeur || undefined).then(res => {
      setClients(res.data.data || res.data || []);
    }).catch(console.error);
  }, [filterRevendeur]);

  // Load projets when client or revendeur changes
  useEffect(() => {
    cascadeAPI.projets(filterClient || undefined, filterRevendeur || undefined).then(res => {
      setProjets(res.data.data || res.data || []);
    }).catch(console.error);
  }, [filterClient, filterRevendeur]);

  // Handlers
  const handleAdd = async (formData) => {
    try {
      await chantiersAPI.create(formData);
      setShowModal(false);
      fetchChantiers();
    } catch (err) {
      console.error("Erreur ajout chantier:", err);
    }
  };

  const handleEdit = async (formData) => {
    try {
      await chantiersAPI.update(editItem._id, formData);
      setEditItem(null);
      fetchChantiers();
    } catch (err) {
      console.error("Erreur modification chantier:", err);
    }
  };

  const handleDelete = async (keepNoeuds = false) => {
    try {
      await chantiersAPI.remove(deleteItem._id, keepNoeuds);
      setDeleteItem(null);
      fetchChantiers();
    } catch (err) {
      console.error("Erreur suppression chantier:", err);
    }
  };

  const toggleCard = (key) => setCardFilter(prev => prev === key ? "all" : key);
  const filteredChantiers = cardFilter === "all" ? chantiers
    : cardFilter === "enCours" ? chantiers.filter(c => c.etat === "En cours")
    : cardFilter === "termines" ? chantiers.filter(c => c.etat === "Terminé")
    : chantiers;

  return (
    <>
      <Header title={t('sites.title')} subtitle={t('sites.subtitle')} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        <StatCard icon={Wrench} iconBg="bg-orange-100 text-orange-600" label={t('sites.totalSites')} value={stats.total} onClick={() => setCardFilter("all")} active={cardFilter === "all"} />
        <StatCard icon={Clock} iconBg="bg-blue-100 text-blue-600" label={t('sites.inProgress')} value={stats.enCours} onClick={() => toggleCard("enCours")} active={cardFilter === "enCours"} />
        <StatCard icon={CheckCircle} iconBg="bg-green-100 text-green-600" label={t('sites.completed')} value={stats.termines} onClick={() => toggleCard("termines")} active={cardFilter === "termines"} />
        <StatCard icon={Users} iconBg="bg-purple-100 text-purple-600" label={t('sites.totalWorkers')} value={stats.ouvriers} onClick={() => navigate('/users')} />
        {canDo('creerChantier') && (
          <Button onClick={() => setShowModal(true)} className="h-full justify-center">
            <Wrench size={18} /> {t('sites.addSite')}
          </Button>
        )}
      </div>

      {!isMembre && (
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
          {adminMode === 'platform' && (
            <select
              value={filterRevendeur}
              onChange={(e) => { setFilterRevendeur(e.target.value); setFilterClient(""); setFilterProjet(""); }}
              className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('common.allResellers')} ({revendeurs.length})</option>
              {revendeurs.map((r) => <option key={r._id} value={r._id}>{r.nom}</option>)}
            </select>
          )}
          <select
            value={filterClient}
            onChange={(e) => { setFilterClient(e.target.value); setFilterProjet(""); }}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('common.allClients')} ({clients.length})</option>
            {clients.map((c) => <option key={c._id} value={c._id}>{c.nom}</option>)}
          </select>
          <select
            value={filterProjet}
            onChange={(e) => setFilterProjet(e.target.value)}
            className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('common.allProjects')} ({projets.length})</option>
            {projets.map((p) => <option key={p._id} value={p._id}>{p.nom}</option>)}
          </select>
        </div>
      )}

      <div className="mb-4">
        <SearchInput value={search} onChange={setSearch} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
          <span className="ml-3 text-sm text-slate-500">{t('sites.loading')}</span>
        </div>
      ) : (
        <Table headers={[t('sites.colSite'), t('sites.colProjectClient'), t('sites.colDates'), t('sites.colState'), t('sites.colSiteManager'), t('sites.colDevices'), t('sites.colActions')]}>
          {filteredChantiers.map((c) => (
            <tr key={c._id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate(`/chantiers/${c._id}`)}>
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Wrench size={16} className="text-orange-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm max-w-xs">{c.nom}</p>
                    <p className="text-xs text-slate-400">ID: {c._id}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <p className="text-sm font-medium text-slate-700">{(typeof c.projet === 'object' ? c.projet?.nom : c.projet) ?? '—'}</p>
                <p className="text-xs text-slate-400">{(typeof c.client === 'object' ? c.client?.nom : c.client) ?? '—'}</p>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1.5 text-xs text-slate-600">
                  <CalendarDays size={12} className="text-slate-400" />
                  {formatDate(c.dateDebut)}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  → {formatDate(c.dateFin)}
                </p>
              </td>
              <td className="px-6 py-4"><Badge label={c.etat} /></td>
              <td className="px-6 py-4">
                <div className="space-y-1">
                  {(c.chefChantier || []).map((m, i) => (
                    <div key={`cc-${i}`} className="flex items-center gap-1.5 text-xs text-blue-600">
                      <UserCircle size={12} className="text-blue-400" /> {m.nom || m}
                    </div>
                  ))}
                  {(c.techniciens || []).map((m, i) => (
                    <div key={`t-${i}`} className="flex items-center gap-1.5 text-xs text-purple-600">
                      <UserCircle size={12} className="text-purple-400" /> {m.nom || m}
                    </div>
                  ))}
                  {(!c.chefChantier?.length && !c.techniciens?.length) && <span className="text-xs text-slate-400">—</span>}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-2">
                  <Cpu size={14} className="text-purple-500" />
                  <span className="text-sm font-medium text-slate-700">{c.appareils}</span>
                </div>
              </td>
              <td className="px-6 py-4" onClick={e => e.stopPropagation()}>
                <ActionMenu
                  rowId={c._id}
                  openMenu={openMenu}
                  setOpenMenu={setOpenMenu}
                  onEdit={() => setEditItem(c)}
                  onDelete={() => setDeleteItem(c)}
                />
              </td>
            </tr>
          ))}
        </Table>
      )}

      {/* Modal suppression chantier avec choix noeuds */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setDeleteItem(null)}>
          <div className="bg-white rounded-2xl p-6 w-[420px] shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-slate-800 text-lg mb-2">{t('sites.deleteSite', 'Supprimer le chantier')}</h3>
            <p className="text-sm text-slate-500 mb-2">
              {t('sites.confirmDelete', 'Voulez-vous supprimer')} <strong>{deleteItem.nom}</strong> ?
            </p>
            {deleteItem.noeuds?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  Ce chantier contient {deleteItem.noeuds.length} nœud(s).
                </p>
                <p className="text-xs text-amber-600">
                  Que souhaitez-vous faire des nœuds installés ?
                </p>
              </div>
            )}
            <div className="flex flex-col gap-2">
              {deleteItem.noeuds?.length > 0 ? (
                <>
                  <button onClick={() => handleDelete(true)}
                    className="w-full px-4 py-2.5 bg-slate-800 text-white rounded-xl text-sm font-medium hover:bg-slate-700 text-left">
                    Supprimer le chantier — garder les nœuds en place
                  </button>
                  <button onClick={() => handleDelete(false)}
                    className="w-full px-4 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 text-left">
                    Supprimer le chantier — remettre les nœuds en stock
                  </button>
                  <button onClick={() => setDeleteItem(null)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">
                    Annuler
                  </button>
                </>
              ) : (
                <div className="flex justify-end gap-3">
                  <button onClick={() => setDeleteItem(null)}
                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">
                    Annuler
                  </button>
                  <button onClick={() => handleDelete(false)}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700">
                    Supprimer
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <EditChantierModal
        isOpen={!!editItem}
        onClose={() => setEditItem(null)}
        onEdit={handleEdit}
        item={editItem}
      />

      <AddChantierModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onAdd={handleAdd}
      />

    </>
  );
}
